import { useInView } from "react-intersection-observer";

import type { ColoredSectionStoryblok } from "@/types/blok-types";
import { useEffect } from "react";

const hexToRgb = (hex: string) => {
  const match = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  return match
    ? `rgb(${parseInt(match[1], 16)}, ${parseInt(match[2], 16)}, ${parseInt(
        match[3],
        16
      )})`
    : null;
};

export const addColor = (color: string) => {
  if (document?.body) {
    document.body.style.background = color;
    document.body.style.setProperty("--color-background", color);
  }
};
export const removeColor = () => {
  if (document?.body) {
    document.body.style.background = "";
    document.body.style.setProperty("--color-background", "var(--color-beige)");
  }
};

const isColored = (color: string) => {
  const isThisColor = document?.body?.style?.background === color;
  const isAnyColor = !!document?.body?.style?.background && !isThisColor;
  const isNoColor = !document?.body?.style?.background;
  return {
    isThisColor,
    isAnyColor,
    isNoColor
  };
};
const addOrRemoveColor = (color: string, inView: boolean) => {
  const { isThisColor, isAnyColor, isNoColor } = isColored(color);
  switch (true) {
    case isThisColor && inView:
      // I'm entering and it's my color, so everything is good!
      break;
    case isThisColor && !inView:
      // I'm exiting, and it's my color, so I'll remove it
      removeColor();
      break;
    case isAnyColor && inView:
      // I'm entering and there is another color, I'll override it
      addColor(color);
      break;
    case isAnyColor && !inView:
      // I'm exiting, but there's another color, so I won't remove it
      break;
    case isNoColor && inView:
      // I'm entering and there is no color, so I'll add it"
      addColor(color);
      break;
    case isNoColor && !inView:
      // Nothing to do
      break;
  }
};

function useColoredSection(color: ColoredSectionStoryblok["color"]) {
  const colorAsRGBString =
    typeof color === "string" ? hexToRgb(color) : hexToRgb(color.toString());

  const { ref } = useInView({
    threshold: 0,
    rootMargin: "-50px",
    onChange: (inView) => {
      if (colorAsRGBString) addOrRemoveColor(colorAsRGBString, inView);
    }
  });

  useEffect(() => {
    return () => {
      removeColor();
    };
  }, []);

  return {
    ref
  };
}

export default useColoredSection;
